import * as React from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useState, useEffect } from "react";
import moment from "moment";
import "moment/locale/es";
import { appConstants } from "../_constants/app.constants";
import { useAuth } from "../auth/AuthContext";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  EmptyState,
  LoadingState,
  PageHeader,
  Toast,
  IconCalendar,
  IconClock,
  IconPlay,
  IconPlus,
  IconTrophy,
} from "../ui";

function getTournamentStatus(tournamentDay) {
  const now = moment().startOf("day");
  const tournamentDate = moment(tournamentDay).startOf("day");
  const daysDiff = tournamentDate.diff(now, "days");

  if (daysDiff < 0) return { status: "En curso", variant: "success", icon: "●" };
  if (daysDiff === 0) return { status: "Hoy", variant: "danger", icon: "◎" };
  if (daysDiff === 1) return { status: "Mañana", variant: "warning", icon: "◔" };
  if (daysDiff <= 7) return { status: `En ${daysDiff} días`, variant: "info", icon: "◷" };
  return { status: `En ${daysDiff} días`, variant: "neutral", icon: "◷" };
}

export default function Tournaments() {
  const { user, ready: authReady } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!authReady || !user) {
      setTournaments([]);
      setLoading(false);
      return;
    }

    const fetchTournaments = async () => {
      try {
        setLoading(true);
        setLoadError("");
        moment.locale("es");
        const mine = await axios.get(`${appConstants.API_URL}/api/tournaments`);
        setTournaments(mine.data);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          setLoadError("Necesitas iniciar sesión para ver tus torneos.");
        } else {
          setLoadError("No se pudieron cargar los torneos.");
        }
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, [authReady, user]);

  const activeTournaments = tournaments
    .filter((tournament) => (tournament.isFinished ?? tournament.IsFinished) !== true)
    .sort((a, b) => moment(a.day).valueOf() - moment(b.day).valueOf());

  return (
    <div className="ui-page-wrap py-4 sm:py-6">
      <PageHeader
        title="Mis torneos"
        subtitle="Donde participas como organizador o colaborador. Ordenados por fecha, los mas proximos primero."
        actions={
          user ? (
            <Button as={RouterLink} to="/crear-torneo" variant="gold">
              <IconPlus size={16} />
              Crear torneo
            </Button>
          ) : null
        }
      />

      {!user && authReady ? (
        <Toast kind="info" className="mt-5">
          Inicia sesión para ver y gestionar los torneos a los que tienes acceso.
        </Toast>
      ) : null}

      {!authReady || loading ? <LoadingState label="Cargando torneos..." className="min-h-[26vh]" /> : null}

      {loadError && !loading && authReady ? <Toast kind="error" className="mt-6">{loadError}</Toast> : null}

      {authReady && !loading && !loadError ? (
        <section className="mt-8">
          {activeTournaments.length === 0 ? (
            <EmptyState
              title="No hay torneos activos"
              description={
                user
                  ? "Crea el primero en un minuto: nombre, fecha y listo."
                  : "No hay torneos activos ahora mismo. Inicia sesión como organizador para crear uno."
              }
              action={
                user ? (
                  <Button as={RouterLink} to="/crear-torneo" variant="gold">
                    <IconPlus size={16} />
                    Crear mi primer torneo
                  </Button>
                ) : null
              }
            />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {activeTournaments.map((tournament, index) => {
                const status = getTournamentStatus(tournament.day);
                const isNext = index === 0;
                return (
                  <Card
                    key={tournament.id}
                    className={isNext ? "border-gold/35 bg-gradient-to-b from-amber-50 to-white shadow-lift" : ""}
                  >
                    <CardBody>
                      {isNext ? (
                        <Badge variant="gold" className="mb-3 uppercase">
                          Próximo torneo
                        </Badge>
                      ) : null}
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-2xl bg-arena/10 p-2 text-arena">
                          <IconTrophy />
                        </div>
                        <h3 className="font-display text-xl font-bold text-ink">{tournament.name}</h3>
                      </div>

                      {tournament.myRole ? (
                        <Badge variant="info" className="mb-3">
                          Rol: {tournament.myRole}
                        </Badge>
                      ) : null}

                      {tournament.description ? (
                        <p className="mb-4 text-sm leading-relaxed text-ink-muted">{tournament.description}</p>
                      ) : null}

                      <div className="rounded-xl border border-arena/10 bg-cream/55 p-3">
                        <div className="flex items-center gap-2 text-sm text-ink-soft">
                          <IconCalendar size={16} />
                          {moment(tournament.day).format("dddd, D [de] MMMM [de] YYYY")}
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-sm text-ink-soft">
                          <IconClock size={16} />
                          {moment(tournament.day).format("HH:mm [hrs]")}
                        </div>
                      </div>

                      <div className="mt-4">
                        <Badge variant={status.variant}>
                          {status.icon} {status.status}
                        </Badge>
                      </div>
                    </CardBody>
                    <CardFooter>
                      <Button
                        as={RouterLink}
                        to={`/tournamentactions/${tournament.id}`}
                        variant={isNext ? "gold" : "primary"}
                        className="w-full"
                      >
                        <IconPlay size={16} />
                        Ver torneo
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
